import React, { useState, useEffect, useRef } from 'react';

function detectSeriesPaste(text) {
    let parts = text.split(/[,.]\s+/).map(p => p.trim()).filter(Boolean);
    if (parts.length < 3) return null;
    if (!/\b(series|collection|set|trilogy|duology|saga|omnibus|box\s*set)\b/i.test(text)) return null;

    let author = '';
    let authorIdx = -1;
    for (let i = parts.length - 1; i >= 0; i--) {
        const m = parts[i].match(/^by\s+(.+)$/i);
        if (m) { author = m[1].trim(); authorIdx = i; break; }
    }

    // Handle "Title by Author" at the end of the last part
    if (!author) {
        const last = parts[parts.length - 1];
        const m = last.match(/^(.+?)\s+by\s+(.+)$/i);
        if (m) {
            author = m[2].trim();
            parts = [...parts.slice(0, -1), m[1].trim()];
        }
    }

    const junk = /^\d+\s+books?\b/i;
    const seriesKw = /\b(series|collection|set|trilogy|duology|saga|omnibus|box\s*set)\b/i;

    const meaningful = parts.filter((p, i) => i !== authorIdx && !junk.test(p));
    if (meaningful.length < 2) return null;

    const seriesIdx = meaningful.findIndex(p => seriesKw.test(p));
    let seriesName, books;
    if (seriesIdx !== -1) {
        seriesName = meaningful[seriesIdx];
        books = meaningful.filter((_, i) => i !== seriesIdx);
    } else {
        seriesName = meaningful[0];
        books = meaningful.slice(1);
    }

    if (books.length === 0) return null;
    return { seriesName, author, books };
}

async function searchOpenLibrary(query) {
    if (!query.trim()) return [];
    try {
        const url = `https://openlibrary.org/search.json?q=${encodeURIComponent(query)}&limit=8&fields=title,author_name,cover_i`;
        const res = await fetch(url);
        if (!res.ok) return [];
        const data = await res.json();
        return (data.docs || []).map(d => ({
            title: d.title,
            author: (d.author_name || [])[0] || '',
            coverId: d.cover_i || null,
        }));
    } catch {
        return [];
    }
}

function SearchResults({ results, searching, onPick, pickLabel }) {
    if (searching) return <div className="search-status">Searching…</div>;
    if (!results.length) return null;
    return (
        <ul className="search-results-list">
            {results.map((r, i) => (
                <li key={i} className="search-result-item" onClick={() => onPick(r)}>
                    {r.coverId && (
                        <img
                            className="search-result-cover"
                            src={`https://covers.openlibrary.org/b/id/${r.coverId}-S.jpg`}
                            alt=""
                        />
                    )}
                    <div className="search-result-info">
                        <div className="search-result-title">{r.title}</div>
                        {r.author && <div className="search-result-author">{r.author}</div>}
                    </div>
                    <span className="search-result-pick">{pickLabel}</span>
                </li>
            ))}
        </ul>
    );
}

export default function AddBookModal({ onClose, onSave, onBulkSave, seriesBookMode = false }) {
    const [type, setType] = useState('book');

    // Book mode state
    const [bookInputMode, setBookInputMode] = useState('search');
    const [manualTitle, setManualTitle] = useState('');
    const [manualAuthor, setManualAuthor] = useState('');
    const [bookQuery, setBookQuery] = useState('');
    const [bookResults, setBookResults] = useState([]);
    const [bookSearching, setBookSearching] = useState(false);
    const [selectedBook, setSelectedBook] = useState(null);

    // Series mode state
    const [seriesTitle, setSeriesTitle] = useState('');
    const [seriesAuthor, setSeriesAuthor] = useState('');
    const [seriesBooks, setSeriesBooks] = useState([]);
    const [seriesInputMode, setSeriesInputMode] = useState('manual');
    const [seriesDraft, setSeriesDraft] = useState('');
    const [seriesQuery, setSeriesQuery] = useState('');
    const [seriesResults, setSeriesResults] = useState([]);
    const [seriesSearching, setSeriesSearching] = useState(false);
    const [authorConflict, setAuthorConflict] = useState(null); // { incoming: string }
    const [seriesSuggestion, setSeriesSuggestion] = useState(null);
    const [bulkBooks, setBulkBooks] = useState([]);
    const [bulkDraft, setBulkDraft] = useState('');
    const [bulkStep, setBulkStep] = useState('review'); // 'review' | 'confirm-series'
    const dismissedTextRef = useRef(null);
    const pendingPickRef = useRef(null);

    const firstRef = useRef(null);

    useEffect(() => {
        if (type !== 'book' || bookInputMode !== 'manual') { setSeriesSuggestion(null); return; }
        if (dismissedTextRef.current === manualTitle) return;
        const suggestion = detectSeriesPaste(manualTitle);
        setSeriesSuggestion(suggestion);
        if (suggestion) setBulkBooks(suggestion.books);
    }, [manualTitle, type, bookInputMode]);

    useEffect(() => {
        firstRef.current?.focus();
        const onKey = e => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    async function runBookSearch() {
        if (!bookQuery.trim()) return;
        setBookSearching(true);
        setBookResults([]);
        setBookResults(await searchOpenLibrary(bookQuery));
        setBookSearching(false);
    }

    async function runSeriesSearch() {
        if (!seriesQuery.trim()) return;
        setSeriesSearching(true);
        setSeriesResults([]);
        setSeriesResults(await searchOpenLibrary(seriesQuery));
        setSeriesSearching(false);
    }

    function addSeriesBookManual() {
        const t = seriesDraft.trim();
        if (!t) return;
        setSeriesBooks(s => [...s, t]);
        setSeriesDraft('');
    }

    function addSeriesBookFromSearch(result) {
        setSeriesBooks(s => [...s, result.title]);
        setSeriesQuery('');
        setSeriesResults([]);
        if (result.author) {
            if (!seriesAuthor.trim()) {
                setSeriesAuthor(result.author);
            } else if (seriesAuthor.trim().toLowerCase() !== result.author.toLowerCase()) {
                setAuthorConflict({ incoming: result.author });
            }
        }
    }

    function handleBookSearchPick(result) {
        const suggestion = detectSeriesPaste(result.title);
        if (suggestion) {
            if (!suggestion.author && result.author) suggestion.author = result.author;
            pendingPickRef.current = result;
            setSeriesSuggestion(suggestion);
            setBulkBooks(suggestion.books);
        } else {
            setSelectedBook(result);
        }
    }

    function dismissSeriesSuggestion() {
        if (pendingPickRef.current) {
            setSelectedBook(pendingPickRef.current);
            pendingPickRef.current = null;
        } else {
            dismissedTextRef.current = manualTitle;
        }
        setSeriesSuggestion(null);
        setBulkBooks([]);
        setBulkDraft('');
        setBulkStep('review');
    }

    function addBulkBook() {
        const t = bulkDraft.trim();
        if (!t) return;
        setBulkBooks(b => [...b, t]);
        setBulkDraft('');
    }

    function handleBulkAddIndividually() {
        if (bulkBooks.length === 0) return;
        const author = seriesSuggestion?.author || '';
        onBulkSave(bulkBooks.map(title => ({ title, author, type: 'book', seriesBooks: [], note: '' })));
        onClose();
    }

    function handleBulkAddAsSeries() {
        if (bulkBooks.length === 0) return;
        onSave({
            title: seriesSuggestion.seriesName,
            author: seriesSuggestion.author || '',
            type: 'series',
            seriesBooks: bulkBooks.map(t => ({ title: t, purchasedBy: null, purchasedAt: null })),
            note: '',
        });
    }

    function submit(e) {
        e.preventDefault();
        if (type === 'book') {
            const title = bookInputMode === 'search' ? selectedBook?.title : manualTitle.trim();
            const author = bookInputMode === 'search' ? selectedBook?.author : manualAuthor.trim();
            if (!title) return;
            onSave({ title, author: author || '', type: 'book', seriesBooks: [], note: '' });
        } else {
            if (!seriesTitle.trim() || seriesBooks.length === 0) return;
            onSave({
                title: seriesTitle.trim(),
                author: seriesAuthor.trim(),
                type: 'series',
                seriesBooks: seriesBooks.map(t => ({ title: t, purchasedBy: null, purchasedAt: null })),
                note: '',
            });
        }
    }

    const canSave = type === 'book'
        ? (bookInputMode === 'search' ? !!selectedBook : !!manualTitle.trim())
        : (!!seriesTitle.trim() && seriesBooks.length > 0);

    return (
        <div className="modal-scrim" onClick={onClose}>
            <form className="modal" onClick={e => e.stopPropagation()} onSubmit={submit}>
                <header className="modal-hd">
                    <h2>{seriesBookMode ? 'Add book to series' : 'Add to your list'}</h2>
                    <button type="button" className="modal-close" onClick={onClose}>close ×</button>
                </header>

                <div className="modal-body">
                    {/* Type toggle — hidden in series-book mode */}
                    {!seriesBookMode && (
                        <div className="gate-field" style={{ marginBottom: 20 }}>
                            <label className="gate-label">Type</label>
                            <div className="toggle-row">
                                <button type="button" className={'toggle-btn' + (type === 'book' ? ' is-on' : '')} onClick={() => setType('book')}>Single book</button>
                                <button type="button" className={'toggle-btn' + (type === 'series' ? ' is-on' : '')} onClick={() => setType('series')}>Series</button>
                            </div>
                        </div>
                    )}

                    {type === 'book' && (
                        <>
                            <div className="gate-field" style={{ marginBottom: 16 }}>
                                <label className="gate-label">How to add</label>
                                <div className="toggle-row">
                                    <button type="button" className={'toggle-btn' + (bookInputMode === 'search' ? ' is-on' : '')} onClick={() => { setBookInputMode('search'); setSelectedBook(null); setBookResults([]); }}>Search</button>
                                    <button type="button" className={'toggle-btn' + (bookInputMode === 'manual' ? ' is-on' : '')} onClick={() => setBookInputMode('manual')}>Manual</button>
                                </div>
                            </div>

                            {bookInputMode === 'search' && !selectedBook && (
                                <div className="gate-field">
                                    <label className="gate-label">Search Open Library</label>
                                    <div className="search-row">
                                        <input
                                            ref={firstRef}
                                            className="gate-input"
                                            type="text"
                                            placeholder="Title or author…"
                                            value={bookQuery}
                                            onChange={e => setBookQuery(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); runBookSearch(); } }}
                                        />
                                        <button type="button" className="search-btn" onClick={runBookSearch} disabled={!bookQuery.trim()}>
                                            Search →
                                        </button>
                                    </div>
                                    <SearchResults results={bookResults} searching={bookSearching} onPick={handleBookSearchPick} pickLabel="select →" />
                                </div>
                            )}

                            {bookInputMode === 'search' && selectedBook && (
                                <div className="search-selected">
                                    <span className="search-selected-label">Selected</span>
                                    <div className="search-selected-title">{selectedBook.title}</div>
                                    {selectedBook.author && <div className="search-selected-author">{selectedBook.author}</div>}
                                    <button type="button" className="search-selected-clear" onClick={() => { setSelectedBook(null); setBookResults([]); }}>
                                        change ×
                                    </button>
                                </div>
                            )}

                            {bookInputMode === 'manual' && (
                                <>
                                    <div className="gate-field">
                                        <label className="gate-label">Title</label>
                                        <input
                                            ref={firstRef}
                                            className="gate-input"
                                            type="text"
                                            placeholder="e.g. The Name of the Wind"
                                            value={manualTitle}
                                            onChange={e => setManualTitle(e.target.value)}
                                        />
                                    </div>
                                    <div className="gate-field">
                                        <label className="gate-label">Author</label>
                                        <input
                                            className="gate-input"
                                            type="text"
                                            placeholder="e.g. Patrick Rothfuss"
                                            value={manualAuthor}
                                            onChange={e => setManualAuthor(e.target.value)}
                                        />
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {type === 'series' && (
                        <>
                            <div className="gate-field">
                                <label className="gate-label">Series name</label>
                                <input
                                    ref={firstRef}
                                    className="gate-input"
                                    type="text"
                                    placeholder="e.g. The Stormlight Archive"
                                    value={seriesTitle}
                                    onChange={e => setSeriesTitle(e.target.value)}
                                />
                            </div>
                            <div className="gate-field">
                                <label className="gate-label">Author</label>
                                <input
                                    className="gate-input"
                                    type="text"
                                    placeholder="e.g. Brandon Sanderson"
                                    value={seriesAuthor}
                                    onChange={e => setSeriesAuthor(e.target.value)}
                                />
                            </div>

                            <div className="gate-field">
                                <div className="series-books-header">
                                    <label className="gate-label">Books in the series</label>
                                    <div className="toggle-row" style={{ marginBottom: 0 }}>
                                        <button type="button" className={'toggle-btn' + (seriesInputMode === 'search' ? ' is-on' : '')} onClick={() => { setSeriesInputMode('search'); setSeriesResults([]); }}>Search</button>
                                        <button type="button" className={'toggle-btn' + (seriesInputMode === 'manual' ? ' is-on' : '')} onClick={() => setSeriesInputMode('manual')}>Manual</button>
                                    </div>
                                </div>

                                {seriesInputMode === 'manual' && (
                                    <div className="series-input-row">
                                        <input
                                            className="gate-input"
                                            type="text"
                                            value={seriesDraft}
                                            placeholder="Book title — Enter to add"
                                            onChange={e => setSeriesDraft(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSeriesBookManual(); } }}
                                        />
                                        <button type="button" className="add-btn" onClick={addSeriesBookManual}>
                                            <span className="add-btn-plus">+</span><span>add</span>
                                        </button>
                                    </div>
                                )}

                                {seriesInputMode === 'search' && (
                                    <div style={{ marginBottom: 8 }}>
                                        <div className="search-row">
                                            <input
                                                className="gate-input"
                                                type="text"
                                                placeholder="Search for a book title…"
                                                value={seriesQuery}
                                                onChange={e => setSeriesQuery(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); runSeriesSearch(); } }}
                                            />
                                            <button type="button" className="search-btn" onClick={runSeriesSearch} disabled={!seriesQuery.trim()}>
                                                Search →
                                            </button>
                                        </div>
                                        <SearchResults results={seriesResults} searching={seriesSearching} onPick={addSeriesBookFromSearch} pickLabel="+ add" />
                                    </div>
                                )}

                                <ul className="series-list">
                                    {seriesBooks.map((t, i) => (
                                        <li className="series-list-item" key={i}>
                                            <span className="series-list-num">{String(i + 1).padStart(2, '0')}</span>
                                            <span>{t}</span>
                                            <button type="button" className="series-list-x" onClick={() => setSeriesBooks(s => s.filter((_, j) => j !== i))}>×</button>
                                        </li>
                                    ))}
                                    {seriesBooks.length === 0 && (
                                        <li style={{ fontStyle: 'italic', color: 'var(--ink-3)', padding: '6px 4px' }}>
                                            No books yet — add at least one.
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </>
                    )}
                </div>

                <footer className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                    <button type="submit" className="btn-primary" disabled={!canSave}>{seriesBookMode ? 'Add to series' : 'Add to list'}</button>
                </footer>

                {seriesSuggestion && bulkStep === 'review' && (
                    <div className="author-conflict-overlay">
                        <div className="author-conflict-card">
                            <div className="author-conflict-heading">Add these books individually?</div>
                            <div className="bulk-add-meta">
                                {seriesSuggestion.seriesName}
                                {seriesSuggestion.author && <> · <em>{seriesSuggestion.author}</em></>}
                            </div>
                            <ul className="series-list">
                                {bulkBooks.map((title, i) => (
                                    <li className="series-list-item" key={i}>
                                        <span className="series-list-num">{String(i + 1).padStart(2, '0')}</span>
                                        <span>{title}</span>
                                        <button type="button" className="series-list-x" onClick={() => setBulkBooks(b => b.filter((_, j) => j !== i))}>×</button>
                                    </li>
                                ))}
                            </ul>
                            <div className="series-input-row">
                                <input
                                    className="gate-input"
                                    type="text"
                                    placeholder="Add another book…"
                                    value={bulkDraft}
                                    onChange={e => setBulkDraft(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addBulkBook(); } }}
                                />
                                <button type="button" className="add-btn" onClick={addBulkBook}>
                                    <span className="add-btn-plus">+</span><span>add</span>
                                </button>
                            </div>
                            <div className="author-conflict-actions">
                                <button type="button" className="btn-secondary" onClick={dismissSeriesSuggestion}>No thanks</button>
                                <button type="button" className="btn-primary" disabled={bulkBooks.length === 0} onClick={() => setBulkStep('confirm-series')}>
                                    Add {bulkBooks.length} book{bulkBooks.length !== 1 ? 's' : ''} →
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {seriesSuggestion && bulkStep === 'confirm-series' && (
                    <div className="author-conflict-overlay">
                        <div className="author-conflict-card">
                            <div className="author-conflict-heading">Are these all part of the same series?</div>
                            <ul className="series-list">
                                {bulkBooks.map((title, i) => (
                                    <li className="series-list-item" key={i}>
                                        <span className="series-list-num">{String(i + 1).padStart(2, '0')}</span>
                                        <span>{title}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="author-conflict-actions">
                                <button type="button" className="btn-secondary" onClick={() => setBulkStep('review')}>← Back</button>
                                <button type="button" className="btn-secondary" onClick={handleBulkAddIndividually}>No, add individually</button>
                                <button type="button" className="btn-primary" onClick={handleBulkAddAsSeries}>Yes, as a series</button>
                            </div>
                        </div>
                    </div>
                )}

                {authorConflict && (
                    <div className="author-conflict-overlay">
                        <div className="author-conflict-card">
                            <div className="author-conflict-heading">Replace series author?</div>
                            <div className="author-conflict-row">
                                <div className="author-conflict-item">
                                    <div className="author-conflict-label">Current</div>
                                    <div className="author-conflict-name">{seriesAuthor}</div>
                                </div>
                                <div className="author-conflict-sep">→</div>
                                <div className="author-conflict-item">
                                    <div className="author-conflict-label">From search</div>
                                    <div className="author-conflict-name">{authorConflict.incoming}</div>
                                </div>
                            </div>
                            <div className="author-conflict-actions">
                                <button type="button" className="btn-secondary" onClick={() => setAuthorConflict(null)}>
                                    Keep current
                                </button>
                                <button type="button" className="btn-primary" onClick={() => { setSeriesAuthor(authorConflict.incoming); setAuthorConflict(null); }}>
                                    Replace
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
}
